import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Users, Briefcase, DollarSign, TrendingUp, User, LogOut, Settings } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [analyticsRes, contractorsRes, jobsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/analytics`),
        axios.get(`${API_URL}/admin/contractors`),
        axios.get(`${API_URL}/admin/jobs`),
      ]);

      setAnalytics(analyticsRes.data);
      setContractors(contractorsRes.data);
      setJobs(jobsRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const verifyContractor = async (contractorId, status) => {
    try {
      await axios.patch(`${API_URL}/admin/contractors/${contractorId}/verify`, null, {
        params: { verification_status: status }
      });
      toast.success('Contractor status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update contractor');
    }
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      toast.error('Failed to log out');
    } finally {
      setLoggingOut(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-neutral-50 flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>ZOLT Admin</h1>
          <div className="flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className="flex items-center gap-2 px-3 py-2 hover:bg-neutral-100 rounded-lg"
                  data-testid="admin-profile-menu"
                >
                  <div className="w-8 h-8 bg-neutral-200 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-neutral-600" />
                  </div>
                  <span className="text-sm text-neutral-700">{user?.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  {user?.name}
                  <p className="text-xs text-neutral-500 font-normal">{user?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  disabled={loggingOut}
                  className="text-red-600 cursor-pointer"
                  data-testid="admin-logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {loggingOut ? 'Logging out...' : 'Log out'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Analytics Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-12">
          <div className="bg-white rounded-xl border border-neutral-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-6 h-6 text-[#007AFF]" />
              <h3 className="font-semibold">Customers</h3>
            </div>
            <p className="text-3xl font-bold">{analytics?.total_customers || 0}</p>
          </div>

          <div className="bg-white rounded-xl border border-neutral-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-6 h-6 text-[#007AFF]" />
              <h3 className="font-semibold">Contractors</h3>
            </div>
            <p className="text-3xl font-bold">{analytics?.total_contractors || 0}</p>
          </div>

          <div className="bg-white rounded-xl border border-neutral-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <Briefcase className="w-6 h-6 text-[#007AFF]" />
              <h3 className="font-semibold">Total Jobs</h3>
            </div>
            <p className="text-3xl font-bold">{analytics?.total_jobs || 0}</p>
          </div>

          <div className="bg-white rounded-xl border border-neutral-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="w-6 h-6 text-[#007AFF]" />
              <h3 className="font-semibold">Revenue</h3>
            </div>
            <p className="text-3xl font-bold">£{analytics?.total_revenue?.toFixed(2) || '0.00'}</p>
          </div>
        </div>

        {/* Pending Contractors */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Pending Contractors</h2>
          <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
            {contractors.filter(c => c.verification_status === 'pending').length === 0 ? (
              <div className="p-8 text-center text-neutral-600">No pending contractors</div>
            ) : (
              <table className="w-full">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Name</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Skills</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Experience</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {contractors.filter(c => c.verification_status === 'pending').map((contractor) => (
                    <tr key={contractor.id} className="border-t border-neutral-200">
                      <td className="px-6 py-4">{contractor.company_name || 'N/A'}</td>
                      <td className="px-6 py-4">{contractor.skills.join(', ')}</td>
                      <td className="px-6 py-4">{contractor.years_experience} years</td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => verifyContractor(contractor.id, 'approved')}
                            className="bg-green-500 hover:bg-green-600 text-white"
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => verifyContractor(contractor.id, 'rejected')}
                          >
                            Reject
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Recent Jobs */}
        <div>
          <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Recent Jobs</h2>
          <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Category</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Price</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Date</th>
                </tr>
              </thead>
              <tbody>
                {jobs.slice(0, 10).map((job) => (
                  <tr key={job.id} className="border-t border-neutral-200">
                    <td className="px-6 py-4">{job.category} - {job.subcategory}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-neutral-100">
                        {job.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4">£{job.estimated_price?.toFixed(2) || '0.00'}</td>
                    <td className="px-6 py-4">{new Date(job.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
